import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Play, Gift } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import HighlightedText from './HighlightedText';
import { Button } from '@/components/ui/button';
import SignUpModal from './SignUpModal';

// Demo content configuration
const STYLE_OPTIONS = [
    { id: "cartoon", label: "Cartoon", mobileLabel: "Cartoon" },
    { id: "realistic", label: "Realistic", mobileLabel: "Photo" },
    { id: "2d", label: "2D", mobileLabel: "2D" },
    { id: "animation", label: "Animation", mobileLabel: "3D" },
] as const;

const CHARACTER_OPTIONS = [
    {
        id: "kuzu",
        label: "Kuzu",
        mobileLabel: "Kuzu",
        description: "A friendly white sheep",
        imageUrl: "https://images.minifil.com/characters/kuzu-thumb.png"
    },
    // {
    //     id: "foxy",
    //     label: "Foxy",
    //     mobileLabel: "Foxy",
    //     description: "A helpful fox friend",
    //     imageUrl: "https://images.minifil.com/characters/foxy-thumb.png"
    // },
    {
        id: "lilly",
        label: "Lilly",
        mobileLabel: "Lilly",
        description: "Cute helper girl",
        imageUrl: "https://images.minifil.com/characters/lilly-thumb.png"
    },
] as const;

// Unified demo data structure
const DEMO_SECTIONS = [
    {
        id: "tasks",
        type: "items" as const,
        items: [
            {
                id: "brush-teeth",
                name: "Brush Teeth",
                images: {
                    kuzu: "https://images.minifil.com/characters/kuzu-brush-400.webp",
                    // foxy: "https://images.minifil.com/characters/foxy-brush-teeth.png",
                    lilly: "https://images.minifil.com/characters/lilly-brush-400.webp",
                },
            },
            {
                id: "eat-breakfast",
                name: "Eat Breakfast",
                images: {
                    kuzu: "https://images.minifil.com/characters/kuzu-porridge-400.webp",
                    // foxy: "https://images.minifil.com/characters/foxy-eat-breakfast.png",
                    lilly: "https://images.minifil.com/characters/lilly-breakfast.webp",
                },
            },
        ],
    },
    {
        id: "shopping",
        type: "items" as const,
        items: [
            {
                id: "apple",
                name: "Apple",
                images: {
                    cartoon: "https://images.minifil.com/styles/cartoon-apple-400.webp",
                    realistic: "https://images.minifil.com/styles/photo-apple-400.webp",
                    "2d": "https://images.minifil.com/styles/2d-apple-400.webp",
                    animation: "https://images.minifil.com/styles/3d-apple-400.webp",
                },
            },
            {
                id: "broccoli",
                name: "Broccoli",
                images: {
                    cartoon: "https://images.minifil.com/styles/cartoon-broccoli-400.webp",
                    realistic: "https://images.minifil.com/styles/photo-broccoli-400.webp",
                    "2d": "https://images.minifil.com/styles/2d-broccoli-400.webp",
                    animation: "https://images.minifil.com/styles/3d-broccoli-400.webp",
                },
            },
        ],
    },
    // {
    //     id: "screenshots",
    //     type: "screenshots" as const,
    //     items: [
    //         {
    //             id: "shopping-list-view",
    //             image: "https://images.minifil.com/screenshots/shopping-list-view.webp",
    //             titleKey: "hero.demo.screenshots.shoppingList.title",
    //             descriptionKey: "hero.demo.screenshots.shoppingList.description"
    //         },
    //         {
    //             id: "task-list-view",
    //             image: "https://images.minifil.com/screenshots/task-list-view.webp",
    //             titleKey: "hero.demo.screenshots.taskList.title",
    //             descriptionKey: "hero.demo.screenshots.taskList.description"
    //         },
    //         {
    //             id: "family-dashboard",
    //             image: "https://images.minifil.com/screenshots/family-dashboard.webp",
    //             titleKey: "hero.demo.screenshots.dashboard.title",
    //             descriptionKey: "hero.demo.screenshots.dashboard.description"
    //         }
    //     ],
    // },
] as const;

type DemoTab = typeof DEMO_SECTIONS[number]['id'];
type DemoSection = typeof DEMO_SECTIONS[number];

interface HeroInteractiveDemoProps {
    className?: string;
}

interface DemoImageMeta {
    url: string;
    altKey: string;
}

const collectDemoImageMetadata = (): DemoImageMeta[] => {
    const metadata: DemoImageMeta[] = [];
    const seen = new Set<string>();

    DEMO_SECTIONS.forEach(section => {
        // Only process items sections since screenshots are commented out
        if (section.type === 'items') {
            section.items.forEach(item => {
                Object.values(item.images).forEach(url => {
                    if (!seen.has(url)) {
                        metadata.push({
                            url,
                            altKey: `hero.demo.${section.id}.${item.id}`,
                        });
                        seen.add(url);
                    }
                });
            });
        }
    });

    return metadata;
};

const DEMO_IMAGE_METADATA = collectDemoImageMetadata();

// Helper function for simplified navigation
const getSectionById = (id: DemoTab): DemoSection => {
    return DEMO_SECTIONS.find(section => section.id === id)!;
};

const HeroInteractiveDemo: React.FC<HeroInteractiveDemoProps> = ({ className = '' }) => {
    // Core state management
    const [activeTab, setActiveTab] = useState<DemoTab>('tasks');
    const [currentItemIndex, setCurrentItemIndex] = useState(0);
    const [currentStyleIndex, setCurrentStyleIndex] = useState(0);
    const [currentScreenshotIndex, setCurrentScreenshotIndex] = useState(0);
    const [imageLoaded, setImageLoaded] = useState<Record<string, boolean>>({});
    const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);

    // Activity tracking for seamless auto/manual transitions
    const [isUserActive, setIsUserActive] = useState(false);

    // Refs for auto-advance and gesture handling
    const autoAdvanceInterval = useRef<NodeJS.Timeout | null>(null);
    const activityTimeout = useRef<NodeJS.Timeout | null>(null);
    const touchStartX = useRef<number>(0);
    const touchStartY = useRef<number>(0);
    const touchStartTime = useRef<number>(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const isUserActiveRef = useRef<boolean>(false);
    const activeTabRef = useRef<DemoTab>('tasks');
    const currentItemIndexRef = useRef<number>(0);
    const currentStyleIndexRef = useRef<number>(0);
    const currentScreenshotIndexRef = useRef<number>(0);

    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    // Handle CTA button click
    const handleCtaClick = () => {
        markUserActivity();
        navigate('/login');
    };

    // Handle Add New button click (opens SignUpModal)
    const handleAddNewClick = () => {
        markUserActivity();
        setIsSignUpModalOpen(true);
    };

    // Mark user as active and reset auto-advance
    const markUserActivity = () => {
        setIsUserActive(true);
        isUserActiveRef.current = true;

        // Clear existing timers
        if (autoAdvanceInterval.current) {
            clearInterval(autoAdvanceInterval.current);
            autoAdvanceInterval.current = null;
        }
        if (activityTimeout.current) {
            clearTimeout(activityTimeout.current);
        }

        // Resume auto-advance after 3 seconds of inactivity
        activityTimeout.current = setTimeout(() => {
            setIsUserActive(false);
            isUserActiveRef.current = false;
            startAutoAdvance();
        }, 3000);
    };

    // Auto-advance logic - seamlessly cycles through content
    const startAutoAdvance = () => {
        if (autoAdvanceInterval.current) {
            clearInterval(autoAdvanceInterval.current);
        }

        autoAdvanceInterval.current = setInterval(() => {
            if (!isUserActiveRef.current) {
                navigateToNext();
            } else {
            }
        }, 2500); // Slightly slower for better viewing
    };

    // Navigation functions - data-driven approach
    const goToStyle = (styleIndex: number) => {
        markUserActivity();
        setCurrentStyleIndex(styleIndex);
    };

    const goToItem = (itemIndex: number) => {
        markUserActivity();
        setCurrentItemIndex(itemIndex);
    };

    const goToTab = (tab: DemoTab) => {
        markUserActivity();
        setActiveTab(tab);
        setCurrentItemIndex(0);
        setCurrentStyleIndex(0);
        setCurrentScreenshotIndex(0);
    };

    // Internal navigation function that doesn't trigger user activity
    const navigateToNext = () => {
        const currentSection = getSectionById(activeTabRef.current);

        // Only handle items sections since screenshots are commented out
        if (currentSection.type === 'items') {
            if (currentSection.id === 'shopping') {
                // For shopping items, use STYLE_OPTIONS
                if (currentStyleIndexRef.current < STYLE_OPTIONS.length - 1) {
                    setCurrentStyleIndex(currentStyleIndexRef.current + 1);
                } else if (currentItemIndexRef.current < currentSection.items.length - 1) {
                    setCurrentItemIndex(currentItemIndexRef.current + 1);
                    setCurrentStyleIndex(0);
                } else {
                    // Go to next section's first item
                    const currentIndex = DEMO_SECTIONS.findIndex(s => s.id === activeTabRef.current);
                    const nextIndex = (currentIndex + 1) % DEMO_SECTIONS.length;
                    const nextSection = DEMO_SECTIONS[nextIndex];
                    setActiveTab(nextSection.id);
                    setCurrentItemIndex(0);
                    setCurrentStyleIndex(0);
                    setCurrentScreenshotIndex(0);
                }
            } else if (currentSection.id === 'tasks') {
                // For tasks, iterate through all tasks for current character first, then move to next character
                if (currentItemIndexRef.current < currentSection.items.length - 1) {
                    setCurrentItemIndex(currentItemIndexRef.current + 1);
                } else if (currentStyleIndexRef.current < CHARACTER_OPTIONS.length - 1) {
                    setCurrentStyleIndex(currentStyleIndexRef.current + 1);
                    setCurrentItemIndex(0);
                } else {
                    // Go to next section's first item
                    const currentIndex = DEMO_SECTIONS.findIndex(s => s.id === activeTabRef.current);
                    const nextIndex = (currentIndex + 1) % DEMO_SECTIONS.length;
                    const nextSection = DEMO_SECTIONS[nextIndex];
                    setActiveTab(nextSection.id);
                    setCurrentItemIndex(0);
                    setCurrentStyleIndex(0);
                    setCurrentScreenshotIndex(0);
                }
            }
        }
    };

    // User-triggered navigation function
    const goToNext = () => {
        markUserActivity();
        navigateToNext();
    };

    const goToPrevious = () => {
        markUserActivity();
        const currentSection = getSectionById(activeTab);

        // Only handle items sections since screenshots are commented out
        if (currentSection.type === 'items') {
            if (currentSection.id === 'shopping') {
                // For shopping items, use STYLE_OPTIONS
                if (currentStyleIndex > 0) {
                    setCurrentStyleIndex(currentStyleIndex - 1);
                } else if (currentItemIndex > 0) {
                    setCurrentItemIndex(currentItemIndex - 1);
                    setCurrentStyleIndex(STYLE_OPTIONS.length - 1);
                } else {
                    // Go to previous section's last item
                    const currentIndex = DEMO_SECTIONS.findIndex(s => s.id === activeTab);
                    const prevIndex = currentIndex === 0 ? DEMO_SECTIONS.length - 1 : currentIndex - 1;
                    const prevSection = DEMO_SECTIONS[prevIndex];
                    setActiveTab(prevSection.id);

                    if (prevSection.type === 'items') {
                        const itemSection = prevSection as Extract<typeof DEMO_SECTIONS[number], { type: 'items' }>;
                        setCurrentItemIndex(itemSection.items.length - 1);
                        // For shopping items, use STYLE_OPTIONS; for tasks, use CHARACTER_OPTIONS
                        const optionsArray = prevSection.id === 'shopping' ? STYLE_OPTIONS : CHARACTER_OPTIONS;
                        setCurrentStyleIndex(optionsArray.length - 1);
                    }
                }
            } else if (currentSection.id === 'tasks') {
                // For tasks, go to previous task for current character, or previous character's last task
                if (currentItemIndex > 0) {
                    setCurrentItemIndex(currentItemIndex - 1);
                } else if (currentStyleIndex > 0) {
                    setCurrentStyleIndex(currentStyleIndex - 1);
                    setCurrentItemIndex(currentSection.items.length - 1);
                } else {
                    // Go to previous section's last item
                    const currentIndex = DEMO_SECTIONS.findIndex(s => s.id === activeTab);
                    const prevIndex = currentIndex === 0 ? DEMO_SECTIONS.length - 1 : currentIndex - 1;
                    const prevSection = DEMO_SECTIONS[prevIndex];
                    setActiveTab(prevSection.id);

                    if (prevSection.type === 'items') {
                        const itemSection = prevSection as Extract<typeof DEMO_SECTIONS[number], { type: 'items' }>;
                        setCurrentItemIndex(itemSection.items.length - 1);
                        // For shopping items, use STYLE_OPTIONS; for tasks, use CHARACTER_OPTIONS
                        const optionsArray = prevSection.id === 'shopping' ? STYLE_OPTIONS : CHARACTER_OPTIONS;
                        setCurrentStyleIndex(optionsArray.length - 1);
                    }
                }
            }
        }
    };

    // Simplified touch gesture handling
    const handleTouchStart = (e: React.TouchEvent) => {
        const touch = e.touches[0];
        touchStartX.current = touch.clientX;
        touchStartY.current = touch.clientY;
        touchStartTime.current = Date.now();
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
        const touch = e.changedTouches[0];
        const endX = touch.clientX;
        const endY = touch.clientY;
        const endTime = Date.now();
        const deltaX = endX - touchStartX.current;
        const deltaY = endY - touchStartY.current;
        const deltaTime = endTime - touchStartTime.current;

        // Check if it's a significant horizontal swipe
        if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50 && deltaTime < 500) {
            if (deltaX > 0) {
                goToPrevious();
            } else {
                goToNext();
            }
        }
    };

    // Keep refs in sync with state
    useEffect(() => {
        isUserActiveRef.current = isUserActive;
        activeTabRef.current = activeTab;
        currentItemIndexRef.current = currentItemIndex;
        currentStyleIndexRef.current = currentStyleIndex;
        currentScreenshotIndexRef.current = currentScreenshotIndex;
    }, [isUserActive, activeTab, currentItemIndex, currentStyleIndex, currentScreenshotIndex]);


    // Initialize auto-advance
    useEffect(() => {
        startAutoAdvance();
        return () => {
            if (autoAdvanceInterval.current) clearInterval(autoAdvanceInterval.current);
            if (activityTimeout.current) clearTimeout(activityTimeout.current);
        };
    }, []);

    // Get current content based on active tab and indices using data-driven approach
    const getCurrentContent = () => {
        const currentSection = getSectionById(activeTab);

        // Only handle items sections since screenshots are commented out
        if (currentSection.type === 'items') {
            const item = currentSection.items[currentItemIndex];
            // For shopping items, use STYLE_OPTIONS; for tasks, use CHARACTER_OPTIONS
            const optionsArray = currentSection.id === 'shopping' ? STYLE_OPTIONS : CHARACTER_OPTIONS;
            const currentOption = optionsArray[currentStyleIndex];

            // Type-safe image access
            const imageUrl = currentSection.id === 'shopping'
                ? item.images[currentOption.id as keyof typeof item.images]
                : item.images[currentOption.id as keyof typeof item.images];

            return {
                image: imageUrl,
                title: t(`hero.demo.${activeTab}.${item.id}`),
                description: t(`hero.demo.${activeTab}.description`)
            };
        }

        // Fallback (should not happen with current configuration)
        return {
            image: '',
            title: '',
            description: ''
        };
    };

    const currentContent = getCurrentContent();

    const imageMetadata = useMemo(() => (
        DEMO_IMAGE_METADATA.map(meta => ({
            url: meta.url,
            alt: t(meta.altKey),
        }))
    ), [t, i18n.language]);

    return (
        <div className={`relative ${className}`}>
            <div className="mx-auto w-full max-w-md md:max-w-xl lg:max-w-2xl xl:max-w-4xl 2xl:max-w-6xl rounded-3xl bg-card/70 p-6 shadow-xl backdrop-blur">

                {/* Tab Navigation - always visible, clean design */}
                <div className="flex items-center justify-center gap-1 mb-4">
                    {DEMO_SECTIONS.map((section) => (
                        <button
                            key={section.id}
                            onClick={() => goToTab(section.id)}
                            className={`px-3 py-1.5 text-xs rounded-full transition-all duration-200 ${activeTab === section.id
                                ? 'bg-emerald-600 text-white shadow-md'
                                : 'bg-card/50 text-muted-foreground hover:bg-card hover:text-foreground'
                                }`}
                        >
                            {t(`hero.demo.tabs.${section.id}`)}
                        </button>
                    ))}
                </div>

                {/* Main content area with embedded controls - full width on xs mobile only */}
                <div className="-mx-6 xs:mx-0">
                    <div
                        ref={containerRef}
                        className="aspect-video w-full overflow-hidden rounded-xl border border-border bg-card relative"
                        style={{ touchAction: 'pan-y' }}
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                    >
                        {/* Main image */}
                        <div className="h-full w-full relative">
                            {imageMetadata.map(({ url, alt }) => {
                                const isActive = currentContent.image === url;
                                return (
                                    <img
                                        key={url}
                                        src={url}
                                        alt={alt}
                                        className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${isActive ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                                        loading="eager"
                                        aria-hidden={!isActive}
                                        onLoad={() => setImageLoaded(prev => ({ ...prev, [url]: true }))}
                                    />
                                );
                            })}

                            {/* Loading state */}
                            {!imageLoaded[currentContent.image] && (
                                <div className="absolute inset-0 animate-pulse bg-linear-to-br from-muted to-muted/50" />
                            )}
                        </div>

                        {/* Navigation arrows */}
                        <div className="absolute inset-0 flex items-center justify-between pointer-events-none">
                            <button
                                onClick={goToPrevious}
                                className="ml-2 p-2 rounded-full bg-black/20 text-white hover:bg-black/40 transition-all duration-200 backdrop-blur-sm pointer-events-auto opacity-70 hover:opacity-100"
                                aria-label="Previous"
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </button>
                            <button
                                onClick={goToNext}
                                className="mr-2 p-2 rounded-full bg-black/20 text-white hover:bg-black/40 transition-all duration-200 backdrop-blur-sm pointer-events-auto opacity-70 hover:opacity-100"
                                aria-label="Next"
                            >
                                <ChevronRight className="h-4 w-4" />
                            </button>
                        </div>

                        {/* Item selector - overlaid on top for shopping/tasks */}
                        {getSectionById(activeTab).type === 'items' && (
                            <div className="absolute top-3 left-3 right-3 flex items-center justify-center gap-1">
                                {(getSectionById(activeTab) as Extract<typeof DEMO_SECTIONS[number], { type: 'items' }>).items.map((item: any, index: number) => (
                                    <button
                                        key={item.id}
                                        onClick={() => goToItem(index)}
                                        className={`px-1 py-0.5 text-[8px] xs:px-1.5 xs:text-[9px] sm-mobile:px-2 sm-mobile:text-[10px] sm:px-2 sm:py-1 sm:text-xs rounded-full transition-all duration-200 backdrop-blur-sm ${index === currentItemIndex
                                            ? 'bg-white/90 text-gray-900 shadow-md font-medium'
                                            : 'bg-black/30 text-white hover:bg-black/50'
                                            }`}
                                    >
                                        <span className="hidden xs:inline">{t(`hero.demo.${activeTab}.${item.id}`)}</span>
                                    </button>
                                ))}
                                {/* Add new button */}
                                <button
                                    onClick={handleAddNewClick}
                                    className="px-1 py-0.5 text-[8px] xs:px-1.5 xs:text-[9px] sm-mobile:px-2 sm-mobile:text-[10px] sm:px-2 sm:py-1 sm:text-xs rounded-full transition-all duration-200 backdrop-blur-sm bg-emerald-500/80 text-white hover:bg-emerald-600/90 border border-emerald-400/50"
                                >
                                    <span className="hidden xs:inline">+ {t('hero.demo.cta.addNew')}</span>
                                </button>
                            </div>
                        )}

                        {/* Style indicators - embedded at bottom for shopping/tasks */}
                        {getSectionById(activeTab).type === 'items' && (
                            <div className="absolute bottom-3 left-3 right-3 flex items-center justify-center gap-2">
                                {(getSectionById(activeTab).id === 'shopping' ? STYLE_OPTIONS : CHARACTER_OPTIONS).map((option, index) => (
                                    <button
                                        key={option.id}
                                        onClick={() => goToStyle(index)}
                                        className={`px-3 py-1.5 rounded-full border transition-all duration-200 backdrop-blur-sm flex items-center justify-center ${index === currentStyleIndex
                                            ? 'border-white bg-white/90 shadow-lg scale-105 text-gray-900'
                                            : 'border-white/50 bg-black/20 hover:bg-black/40 hover:border-white/70 text-white'
                                            }`}
                                        title={t(`hero.style.${option.id.toLowerCase()}`)}
                                    >
                                        <span className="text-xs font-medium">
                                            {/* Show full label on desktop, mobile label on mobile */}
                                            <span className="hidden sm:inline">{option.label}</span>
                                            <span className="sm:hidden">{option.mobileLabel}</span>
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Screenshot navigation dots - commented out since screenshots section is disabled */}
                        {/* {getSectionById(activeTab).type === 'screenshots' && (
                        <div className="absolute bottom-3 left-3 right-3 flex items-center justify-center gap-2">
                            {(getSectionById(activeTab) as Extract<typeof DEMO_SECTIONS[number], { type: 'screenshots' }>).items.map((_: any, index: number) => (
                                <button
                                    key={index}
                                    onClick={() => {
                                        markUserActivity();
                                        setCurrentScreenshotIndex(index);
                                    }}
                                    className={`w-2 h-2 rounded-full transition-all duration-200 ${index === currentScreenshotIndex
                                        ? 'bg-white scale-125 shadow-lg'
                                        : 'bg-white/50 hover:bg-white/70'
                                        }`}
                                />
                            ))}
                        </div>
                    )} */}

                        {/* Auto-advance indicator */}
                        {!isUserActive && (
                            <div className="absolute top-3 right-3">
                                <div className="w-6 h-6 rounded-full bg-emerald-400 flex items-center justify-center" title="Auto-playing">
                                    <Play className="h-3 w-3 text-white fill-white" />
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Content description */}
                <div className="mt-4 text-center">
                    <h2 className="text-lg font-semibold text-foreground mb-2">
                        <HighlightedText text={currentContent.title} />
                    </h2>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                        <HighlightedText text={currentContent.description} />
                    </p>
                </div>

                {/* CTA section - shown after shopping and tasks sections */}
                <div className="mt-4 text-center">
                    <Button
                        onClick={handleCtaClick}
                        className="bg-linear-to-r from-emerald-500 to-sky-500 hover:from-emerald-600 hover:to-sky-600 text-white px-6 py-2 text-sm font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                    >
                        <Gift className="mr-2 h-4 w-4" />
                        {t('hero.demo.cta.button')}
                    </Button>
                </div>
            </div>

            {/* SignUp Modal */}
            <SignUpModal
                open={isSignUpModalOpen}
                onOpenChange={setIsSignUpModalOpen}
            />
        </div>
    );
};

export default HeroInteractiveDemo;
